"use client"

import { useSyncExternalStore } from "react"

/**
 * Live PDF-viewer state (page/zoom/search/url), keyed per tab so two PDF
 * Preview tabs open at once each keep their own — written by whichever
 * synctex-pdf-viewer.tsx instance owns that tab id, read by
 * pdf-ribbon-toolbar.tsx filtered down to whichever tab currently has focus
 * (setActivePdfTab, called from editor-ribbon-bar.tsx via layout-context.tsx's
 * activeTabId). A plain module-level singleton (mirrors lib/upload-job-store.ts)
 * rather than editor-store.tsx state: this is ephemeral view state with
 * nothing to persist, undo, or CRDT-sync.
 */
export type PdfViewerState = {
  /** The PDF currently loaded in that tab's iframe — null while nothing's compiled/open yet. */
  url: string | null
  page: number
  pageCount: number
  /** 100 == the document's default fit-to-view scale (matches pdfrx's own zoom baseline). */
  zoomPercent: number
  /** Null until a search has actually been run; 0 is a genuine "no matches". */
  searchMatchCount: number | null
  /** 0-based index of the current match, or null if there isn't one. */
  searchCurrentIndex: number | null
  /** What's typed in the ribbon's search box for this specific tab — kept
   *  here (not local component state in pdf-ribbon-toolbar.tsx) so switching
   *  to a different PDF tab shows *that* tab's query, not whatever was last
   *  typed while another tab was active. */
  searchQuery: string
}

const DEFAULT_STATE: PdfViewerState = {
  url: null,
  page: 1,
  pageCount: 0,
  zoomPercent: 100,
  searchMatchCount: null,
  searchCurrentIndex: null,
  searchQuery: "",
}

const statesByTab = new Map<string, PdfViewerState>()
let activeTabId: string | null = null
let activeSnapshot: PdfViewerState | null = null
const listeners = new Set<() => void>()

function notify() {
  activeSnapshot = activeTabId ? (statesByTab.get(activeTabId) ?? null) : null
  for (const listener of listeners) listener()
}

export function patchPdfViewerState(tabId: string, patch: Partial<PdfViewerState>) {
  statesByTab.set(tabId, { ...(statesByTab.get(tabId) ?? DEFAULT_STATE), ...patch })
  notify()
}

export function clearPdfViewerState(tabId: string) {
  statesByTab.delete(tabId)
  notify()
}

/** Which tab's state usePdfViewerState() reads — the PDF Preview tab that
 *  currently has focus, or null when none does (editor/Console focused). */
export function setActivePdfTab(tabId: string | null) {
  if (activeTabId === tabId) return
  activeTabId = tabId
  notify()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return activeSnapshot
}

export function usePdfViewerState(): PdfViewerState | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
